import type { PlanType } from '@/lib/plans'
import { useFeaturePaywallStore } from '@/stores/feature-paywall'

import { useHasFeatureAccess } from '../use-has-feature-access'

interface UseFeaturePaywallReturn {
  hasAccess: boolean
  requireAccess: (callback: () => void) => void
}

/**
 * Hook to manage feature access and feature paywall display
 * @param feature - The feature identifier (e.g., FEATURES.SANDBOX)
 * @param minimumPlan - The minimum plan required for the feature (e.g., PLANS.PRO)
 * @param source - Optional source identifier for tracking where the paywall was triggered (e.g., 'settings/webhooks')
 * @param options - `useManagedAccount: true` gates on the managed account while managing one (for /settings account-admin features); default uses the primary account
 * @returns Object with hasAccess boolean and requireAccess function
 *
 * @example
 * ```ts
 * const { hasAccess, requireAccess } = useFeaturePaywall(FEATURES.SANDBOX, PLANS.PRO, 'settings/sandbox')
 *
 * const handleAction = () => {
 *   requireAccess(() => {
 *     // This code only runs if user has access
 *     performAction()
 *   })
 * }
 * ```
 */
export function useFeaturePaywall(
  feature: string,
  minimumPlan: PlanType,
  source?: string,
  options?: { useManagedAccount?: boolean },
): UseFeaturePaywallReturn {
  const showFeaturePaywall = useFeaturePaywallStore(
    (state) => state.showFeaturePaywall,
  )
  const hasAccess = useHasFeatureAccess(minimumPlan, options)

  const requireAccess = (callback: () => void) => {
    if (!hasAccess) {
      showFeaturePaywall(feature, minimumPlan, source)
      return
    }
    callback()
  }

  return {
    hasAccess,
    requireAccess,
  }
}
